import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Icon } from "./brand";
import { fmtMoney } from "../utils/money";
import { resolveMime } from "../utils/fileMime";

// The one area a client's verified POA sees (backend: routes/delegation.py).
// They sign in as themselves (role "delegate"), never as the client, and the
// backend refuses every route outside /delegate/* for their session
// (core/auth_deps.py), so nothing here may call anything else.
//
// Read-only except for uploads, which wait for the advisor's review before
// the client sees them, and a conversation with the advisor. That
// conversation is the POA's own: they never see the client's messages.

interface ClientRow {
  client_id: number;
  client_name: string;
  advisor_name: string;
  can_view_plan: boolean;
  can_view_documents: boolean;
  can_upload: boolean;
  can_message: boolean;
}
interface ConvoMsg { id: number; content: string; from_poa: boolean; mine: boolean; created_at: string | null }
interface Summary {
  client_name: string;
  balances: Record<string, number | null>;
  goals: { name: string; target: number; target_date: string | null }[];
}
interface Doc { id: number; file_name: string; uploaded_at: string | null; category: string | null }

const card: React.CSSProperties = {
  background: "white", border: "1px solid #E5E7EB", borderRadius: 4, padding: "20px 22px", marginBottom: 16,
};
const h2: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: "#111827", margin: "0 0 12px" };
const muted: React.CSSProperties = { fontSize: 13, color: "#6B7280", lineHeight: 1.5 };
const btn: React.CSSProperties = {
  padding: "6px 12px", borderRadius: 4, border: "1px solid #E5E7EB", background: "white",
  color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};

const BALANCE_ROWS: [string, string][] = [
  ["cash", "Cash"], ["taxable_investments", "Investments (taxable)"], ["tax_deferred", "Retirement (tax-deferred)"],
  ["tax_free", "Retirement (tax-free)"], ["real_estate", "Real estate"], ["other_assets", "Other assets"],
  ["credit_card_debt", "Credit cards"], ["loans", "Loans"], ["other_liabilities", "Other debts"],
];

const fmtDate = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const readB64 = (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = () => reject(r.error);
  r.readAsDataURL(file);
});

function saveB64(b64: string, mime: string, filename: string) {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime || "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// `initialClientId` / `onExit`: a client who is also a POA opens this from the
// account chooser (ActingAs.tsx) and can go back to their own account.
export default function DelegatePortal({ onLogout, initialClientId, onExit }: {
  onLogout: () => void; initialClientId?: number; onExit?: () => void;
}) {
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [active, setActive] = useState<ClientRow | null>(null);

  useEffect(() => {
    api.get("/delegate/clients")
      .then((r: any) => {
        const list: ClientRow[] = Array.isArray(r) ? r : [];
        setClients(list);
        const start = list.find(c => c.client_id === initialClientId) || (list.length === 1 ? list[0] : null);
        if (start) setActive(start);
      })
      .catch(() => setClients([]));
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#F9FAFB", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Power of attorney access
            </p>
            <h1 style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 800, color: "#111827" }}>
              {active ? `Acting for ${active.client_name}` : "Who you act for"}
            </h1>
          </div>
          {active && clients && clients.length > 1 && (
            <button style={btn} onClick={() => setActive(null)}>Switch</button>
          )}
          {onExit && <button style={btn} onClick={onExit}>Back to my account</button>}
          <button style={btn} onClick={onLogout}>Sign out</button>
        </div>

        {clients === null && <p style={muted}>Loading…</p>}
        {clients && clients.length === 0 && (
          <div style={card}>
            <h2 style={h2}>No active access</h2>
            <p style={{ ...muted, margin: 0 }}>
              Your access has ended or hasn't been set up yet. Contact the advisor who invited you.
            </p>
          </div>
        )}
        {clients && clients.length > 1 && !active && clients.map(c => (
          <button key={c.client_id} onClick={() => setActive(c)}
            style={{ ...card, width: "100%", textAlign: "left", cursor: "pointer", display: "block", fontFamily: "inherit" }}>
            <strong style={{ fontSize: 14, color: "#111827" }}>{c.client_name}</strong>
            <span style={{ ...muted, display: "block" }}>Advisor: {c.advisor_name}</span>
          </button>
        ))}
        {active && <ClientView c={active} />}
      </div>
    </div>
  );
}

function ClientView({ c }: { c: ClientRow }) {
  return (
    <>
      <p style={{ ...muted, margin: "0 0 16px" }}>
        You can see {c.client_name}'s information{c.can_upload ? " and send documents to " + (c.advisor_name || "their advisor") : ""}.
        You can't change anything in their account. Every visit is recorded.
      </p>
      {c.can_view_plan && <SummaryCard clientId={c.client_id} />}
      {(c.can_view_documents || c.can_upload) && <DocumentsCard c={c} />}
      {c.can_message && <MessagesCard c={c} />}
    </>
  );
}

function SummaryCard({ clientId }: { clientId: number }) {
  const [s, setS] = useState<Summary | null | "error">(null);
  useEffect(() => {
    api.get(`/delegate/clients/${clientId}/summary`).then((r: any) => setS(r)).catch(() => setS("error"));
  }, [clientId]);
  if (s === null) return <div style={card}><p style={{ ...muted, margin: 0 }}>Loading…</p></div>;
  if (s === "error") return <div style={card}><p style={{ ...muted, margin: 0 }}>Couldn't load the summary.</p></div>;
  const b = s.balances;
  return (
    <div style={card}>
      <h2 style={h2}>Summary</h2>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
        {([["Net worth", b.net_worth], ["Assets", b.total_assets], ["Debts", b.total_liabilities]] as [string, number | null][]).map(([label, v]) => (
          <div key={label}>
            <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>${fmtMoney(Number(v || 0))}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gap: 2 }}>
        {BALANCE_ROWS.filter(([k]) => Number(b[k] || 0) !== 0).map(([k, label]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#374151", padding: "3px 0" }}>
            <span>{label}</span><span>${fmtMoney(Number(b[k] || 0))}</span>
          </div>
        ))}
      </div>
      {s.goals.length > 0 && (
        <>
          <h2 style={{ ...h2, margin: "18px 0 8px", fontSize: 13 }}>Goals</h2>
          {s.goals.map((g, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#374151", padding: "3px 0" }}>
              <span>{g.name}</span>
              <span>${fmtMoney(g.target)}{g.target_date ? ` by ${fmtDate(g.target_date)}` : ""}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function DocumentsCard({ c }: { c: ClientRow }) {
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    if (!c.can_view_documents) { setDocs([]); return; }
    api.get(`/delegate/clients/${c.client_id}/documents`).then((r: any) => setDocs(Array.isArray(r) ? r : [])).catch(() => setDocs([]));
  }, [c]);
  useEffect(() => { load(); }, [load]);

  const download = async (d: Doc) => {
    try {
      const r: any = await api.get(`/delegate/clients/${c.client_id}/documents/${d.id}`);
      saveB64(r.file_data, r.mime_type, r.file_name || d.file_name);
    } catch {
      setNote("Couldn't download that file.");
    }
  };

  const upload = async (files: FileList) => {
    setBusy(true); setNote("");
    let ok = 0;
    for (const f of Array.from(files)) {
      try {
        const file_data = await readB64(f);
        await api.post(`/delegate/clients/${c.client_id}/documents`, {
          file_name: f.name, file_data, file_size: f.size, mime_type: resolveMime(f),
        });
        ok++;
      } catch (e: any) {
        setNote(`${f.name}: ${e?.message || "didn't upload"}`);
      }
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
    if (ok) setNote(`Sent ${ok} file${ok === 1 ? "" : "s"} to ${c.advisor_name || "the advisor"}. They'll review before it's added.`);
  };

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <h2 style={{ ...h2, margin: 0, flex: 1 }}>Documents</h2>
        {c.can_upload && (
          <button style={btn} disabled={busy} onClick={() => fileRef.current?.click()}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="upload" size={12} /> {busy ? "Uploading…" : "Send a document"}</span>
          </button>
        )}
        <input ref={fileRef} type="file" multiple style={{ display: "none" }}
          onChange={e => e.target.files && upload(e.target.files)} />
      </div>
      {note && <p style={{ ...muted, margin: "0 0 10px" }}>{note}</p>}
      {c.can_view_documents ? (
        docs === null ? <p style={{ ...muted, margin: 0 }}>Loading…</p>
          : docs.length === 0 ? <p style={{ ...muted, margin: 0 }}>No documents yet.</p>
          : docs.map(d => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #F3F4F6" }}>
              <Icon name="doc" size={13} style={{ color: "#6B7280", flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.file_name}</span>
              <span style={{ fontSize: 11, color: "#9CA3AF" }}>{fmtDate(d.uploaded_at)}</span>
              <button style={btn} onClick={() => download(d)}>Download</button>
            </div>
          ))
      ) : (
        <p style={{ ...muted, margin: 0 }}>You can send documents but not view the client's files.</p>
      )}
    </div>
  );
}

function MessagesCard({ c }: { c: ClientRow }) {
  const [msgs, setMsgs] = useState<ConvoMsg[] | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/delegate/clients/${c.client_id}/messages`)
      .then((r: any) => setMsgs(Array.isArray(r) ? r : []))
      .catch(() => setMsgs([]));
  }, [c]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true); setError("");
    try {
      await api.post(`/delegate/clients/${c.client_id}/messages`, { content: text });
      setDraft("");
      load();
    } catch (e: any) {
      setError(e?.message || "Couldn't send");
    } finally {
      setSending(false);
    }
  };

  const advisor = c.advisor_name || "the advisor";
  return (
    <div style={card}>
      <h2 style={h2}>Messages with {advisor}</h2>
      <p style={{ ...muted, margin: "0 0 10px" }}>{c.client_name} can see this conversation too.</p>
      {msgs === null ? <p style={{ ...muted, margin: 0 }}>Loading…</p>
        : msgs.length === 0 ? <p style={{ ...muted, margin: "0 0 10px" }}>No messages yet.</p>
        : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
            {msgs.map(m => (
              <div key={m.id} style={{ alignSelf: m.mine ? "flex-end" : "flex-start", maxWidth: "80%" }}>
                <div style={{
                  padding: "8px 11px", borderRadius: 4, fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap",
                  background: m.mine ? "var(--brand-primary)" : "#F3F4F6", color: m.mine ? "white" : "#111827",
                }}>{m.content}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", textAlign: m.mine ? "right" : "left", marginTop: 2 }}>
                  {m.mine ? "You" : advisor} · {fmtDate(m.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      <textarea value={draft} onChange={e => setDraft(e.target.value)} maxLength={5000} rows={3}
        placeholder={`Write to ${advisor}`}
        style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #E5E7EB", borderRadius: 4, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
      {error && <p style={{ ...muted, color: "#DC2626", margin: "6px 0 0" }}>{error}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button style={{ ...btn, background: "var(--brand-primary)", color: "white", border: "none", opacity: sending || !draft.trim() ? 0.6 : 1 }}
          disabled={sending || !draft.trim()} onClick={send}>
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}
